/**
 * WHAT KIND OF RV IT IS.
 *
 * Anthony, 2026-09-26: an RV trade version of this desk, "with RV photo
 * tiles on mobile."
 *
 * ─── WHY THE CLASS AND NOT THE MAKE ────────────────────────────────────
 *
 * An auto shop recognizes a car by its make and model, which is why
 * public/images/cars is keyed that way. An RV tech does not. A Thor, a
 * Winnebago and a Jayco can all be a Class C, and what decides the job --
 * whether there is an engine at all, whether the roof is reachable from a
 * ladder, whether the slide is hydraulic -- is the class. So the picture
 * beside an RV job is a picture of its class, and the vehicles screen can
 * be browsed by it.
 *
 * ─── STORED IN `trim`, NOT A NEW COLUMN ─────────────────────────────────
 *
 * The same move garage_door made with `trim` for the material. The column
 * is free text, has existed since 0001, and is shown on the job card under
 * the trade's own label, so an RV shop reads "Class" there. Nothing new in
 * the schema means nothing new for the Railway build or the old Netlify
 * desk that shares this database.
 *
 * Free text means the reading has to be forgiving: "class a", "Class-A",
 * "5th wheel" and "fifth wheeler" are all somebody typing the same thing.
 * `rvClassOf` is the one place that decides, so the tile filter and the
 * thumbnail cannot disagree about which RV is which.
 *
 * Pure and import free, like trade.ts, because the server may say these
 * words too.
 */

export type RvClassKey =
  | "class_a" | "class_b" | "class_c" | "fifth_wheel" | "travel_trailer"
  | "toy_hauler" | "truck_camper" | "popup";

export type RvClass = {
  key: RvClassKey;
  /** What a tech calls it out loud, and what the tile says. */
  label: string;
  /** What gets written into `trim` when somebody picks this tile. */
  canonical: string;
  /**
   * The ways it gets typed, lower case, punctuation already stripped.
   * Checked as whole words against the cleaned text.
   */
  aliases: string[];
  /** Whether it drives itself. Towables have no engine and no odometer. */
  motorized: boolean;
};

/** ORDER IS THE TILE ORDER: the motorhomes, then the towables by size. */
export const RV_CLASSES: readonly RvClass[] = [
  { key: "class_a", label: "Class A", canonical: "Class A",
    aliases: ["class a", "a class", "motorcoach", "motor coach", "diesel pusher", "pusher"],
    motorized: true },
  { key: "class_c", label: "Class C", canonical: "Class C",
    aliases: ["class c", "c class", "super c", "class c plus"],
    motorized: true },
  { key: "class_b", label: "Class B", canonical: "Class B",
    aliases: ["class b", "b class", "camper van", "campervan", "van camper", "sprinter camper",
      "class b plus"],
    motorized: true },
  { key: "toy_hauler", label: "Toy hauler", canonical: "Toy hauler",
    aliases: ["toy hauler", "toyhauler", "toy box"],
    motorized: false },
  { key: "fifth_wheel", label: "Fifth wheel", canonical: "Fifth wheel",
    aliases: ["fifth wheel", "5th wheel", "fifth wheeler", "5th wheeler", "5w", "fiver"],
    motorized: false },
  { key: "travel_trailer", label: "Travel trailer", canonical: "Travel trailer",
    aliases: ["travel trailer", "bumper pull", "tt", "trailer", "teardrop", "airstream"],
    motorized: false },
  { key: "truck_camper", label: "Truck camper", canonical: "Truck camper",
    aliases: ["truck camper", "slide in", "slidein", "cabover camper"],
    motorized: false },
  { key: "popup", label: "Pop-up", canonical: "Pop-up",
    aliases: ["pop up", "popup", "tent trailer", "fold down", "folding camper"],
    motorized: false },
];

/**
 * THE MATCH ORDER, which is not the tile order. The first class whose
 * alias appears wins, so the more particular kinds go first:
 *
 *   - toy hauler before fifth wheel and travel trailer, because a "fifth
 *     wheel toy hauler" is a toy hauler to the tech -- the garage and the
 *     ramp door are the part that breaks;
 *   - pop-up and truck camper before travel trailer, because "tent
 *     trailer" contains the word trailer and is not one.
 */
const MATCH_FIRST: RvClassKey[] = ["toy_hauler", "popup", "truck_camper"];
const MATCH_ORDER: readonly RvClass[] = [
  ...RV_CLASSES.filter((c) => MATCH_FIRST.includes(c.key)),
  ...RV_CLASSES.filter((c) => !MATCH_FIRST.includes(c.key)),
];

/** Lower case, anything that is not a letter or digit becomes one space. */
const clean = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/**
 * WHICH CLASS THIS TEXT NAMES, or null when it names none.
 *
 * Whole words only, so "Class Adventurer" is not a Class A and the "tt" in
 * a model number is not a travel trailer. Padded with spaces on both sides
 * so an alias at the very start or end still counts as a whole word.
 */
export function rvClassOf(...texts: Array<string | null | undefined>): RvClassKey | null {
  const hay = ` ${texts.map((t) => clean(String(t ?? ""))).filter(Boolean).join(" ")} `;
  if (!hay.trim()) return null;
  for (const c of MATCH_ORDER) {
    if (c.aliases.some((a) => hay.includes(` ${a} `))) return c.key;
  }
  return null;
}

/** The class record for a key, or null. */
export function rvClass(key: string | null | undefined): RvClass | null {
  return RV_CLASSES.find((c) => c.key === key) ?? null;
}

/**
 * THE PICTURE FOR A CLASS, or the generic RV when the class is unknown.
 *
 * A photograph where RV Travel Agency has one. Anthony, 2026-09-26, asked
 * whether to use them: "Use RVTA photos". They are the studio side views
 * rvtravelagency.com already shows for these models, one per class, on
 * white, about 20-30KB at 760 wide, copied into public/images/rv as
 * <class>.webp.
 *
 * RVTA has no truck camper and no pop-up, so those two and the unclassed
 * RV keep the drawings tools/rv-art.mjs makes. The drawings for the other
 * six stay on disk too: taking a class out of RV_PHOTOS puts its drawing
 * back without redrawing anything.
 */
const RV_PHOTOS: ReadonlySet<RvClassKey> = new Set<RvClassKey>([
  "class_a", "class_b", "class_c", "fifth_wheel", "travel_trailer", "toy_hauler",
]);

export function rvArt(key: RvClassKey | null): string {
  if (!key) return "/images/rv/rv.svg";
  return `/images/rv/${key.replace(/_/g, "-")}.${RV_PHOTOS.has(key) ? "webp" : "svg"}`;
}

/**
 * A PostgREST `or` filter that finds every vehicle of a class in `trim`.
 *
 * Built from the same aliases rvClassOf reads, so the tile and the
 * thumbnail agree. ilike cannot say "whole word", so two kinds of alias
 * stay out of the database filter:
 *
 *   - the short ones ("tt", "5w"), which turn up inside model numbers;
 *   - any alias that sits inside ANOTHER class's alias. "trailer" is in
 *     "tent trailer" and "toy hauler trailer" both, and a Travel trailer
 *     tile that lists every pop-up is worse than one that misses a tech's
 *     shorthand.
 *
 * The tile's own label is always included, and it is what gets written
 * when somebody tags an RV, so anything tagged the ordinary way is found.
 * Values are double quoted because PostgREST reads a bare space or comma
 * inside `or=(...)` as syntax.
 */
export function rvTrimFilter(key: RvClassKey): string {
  const c = rvClass(key);
  if (!c) return "";
  const others = RV_CLASSES.filter((o) => o.key !== key).flatMap((o) => o.aliases);
  const terms = [c.canonical.toLowerCase(), ...c.aliases]
    .filter((a) => a.length > 3)
    .filter((a) => !others.some((o) => o.includes(a)))
    .map((a) => a.replace(/["\\,()%*]/g, ""));
  return [...new Set(terms)].map((a) => `trim.ilike."%${a}%"`).join(",");
}
